import { useCallback, useEffect, useState } from "react";
import type {
  InstanceActionRole,
  InstanceActionRolesView,
  MemberAttributeAccess,
  MemberAttributeAccessView,
  InstanceStatusRole,
  InstanceStatusRolePalette,
  InstanceStatusRolesView,
} from "@keydials/api-client";
import { ApiError, STATUS_AUDIENCES, STATUS_AUDIENCE_LABELS, type StatusAudience } from "@keydials/api-client";
import { INSTANCE_ACTIONS, INSTANCE_ROLE_NAMES, instanceRoleDescription } from "@keydials/ui";
import { classifyVideoUrl, VideoPlayer } from "@keydials/dial-workspace";
import { SlideOver } from "./Modal";
import { SettingsCard } from "../../settings/SettingsCard";
import { GripGlyph } from "../GripGlyph";
import { Icon } from "../Icon";
import { Avatar } from "../primitives";
import { hexA, personFrom, type Person } from "../../lib/ui";
import { api, getAccessToken } from "../../auth/AuthContext";
import { HELP_VIDEOS_DOCK_POSITION_KEY } from "../../help/dockPosition";
import { useDraggableDock } from "../../help/useDraggableDock";

// ---- Per-member attribute exceptions (KEY-299), mounted from the Members settings section ----


export function MemberFineTune({ instanceId, userId, name }: { instanceId: string; userId: string; name: string }) {
  const [view, setView] = useState<MemberAttributeAccessView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      setView(await api.getMemberAttributeAccess(token, instanceId, userId));
      setError(null);
    } catch {
      setError("Couldn't load this person's field access.");
    }
  }, [instanceId, userId]);

  useEffect(() => {
    setView(null);
    void load();
  }, [load]);

  async function setAccess(a: MemberAttributeAccess, access: string | null) {
    const token = getAccessToken();
    if (!token) return;
    try {
      await api.setMemberAttributeAccess(token, instanceId, userId, [
        { target: a.target, attributeKey: a.attributeKey, access },
      ]);
      await load();
    } catch {
      setError("Couldn't save that change.");
    }
  }

  const exceptions = view?.attributes.filter((a) => a.access !== null).length ?? 0;

  return (
    <div style={{ marginTop: 10, padding: "12px 14px", border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--surface-2)" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 2 }}>
        Fields for {name}
        {exceptions > 0 && (
          <span style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 600, color: "var(--accent-text)" }}>
            {exceptions} exception{exceptions === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--text-faint)" }}>
        Default follows the dial's rules. An explicit choice wins either way — and never applies to an Admin.
      </p>
      {error && <p style={{ margin: "0 0 8px", fontSize: 12.5, color: "var(--st-overdue)" }}>{error}</p>}
      {view === null && !error && <p style={{ margin: 0, fontSize: 13, color: "var(--text-faint)" }}>Loading…</p>}
      {view?.attributes.length === 0 && (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-faint)" }}>This dial declares no fields.</p>
      )}
      {view?.attributes.map((a) => (
        <div key={`${a.target}:${a.attributeKey}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--text)", minWidth: 120, flex: "1 1 120px" }}>
            {a.label}
            <span style={{ marginLeft: 6, fontSize: 11, color: "var(--text-faint)" }}>{a.target}</span>
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            {([
              { value: null, label: "Default" },
              { value: "See", label: "See" },
              { value: "Edit", label: "Edit" },
              { value: "Hidden", label: "Hidden" },
            ] as const).map((choice) => {
              const on = a.access === choice.value;
              return (
                <button
                  key={choice.label}
                  type="button"
                  aria-pressed={on}
                  onClick={() => void setAccess(a, choice.value)}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "3px 9px",
                    borderRadius: 99,
                    cursor: "pointer",
                    border: `var(--border-w) solid ${on ? "var(--accent)" : "var(--border)"}`,
                    background: on ? hexA("#2563eb", 0.14) : "var(--surface)",
                    color: on ? "var(--text)" : "var(--text-faint)",
                  }}
                >
                  {choice.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Status roles modal (per-instance override of who may set each status) ----

const statusRoleKey = (statusSetId: string, statusKey: string) => `${statusSetId}::${statusKey}`;

/**
 * Who may set each status in THIS dial. The dial definition ships a default set of roles per status; a dial
 * admin can narrow or widen them here without affecting any other dial built from the same definition.
 *
 * Admin is deliberately fixed on: an admin bypasses the role check on the server anyway, so allowing it to be
 * unticked would show a restriction that isn't real — and would let a status end up settable by nobody.
 */
/**
 * Who may move each status on this dial, plus the KEY-298 action matrix beside it.
 *
 * Was a modal behind a top-bar button; it is now a section of the Settings tab. Nothing about the editing
 * changed — only where it lives — so the save/discard pair still sits at the bottom of its own body rather
 * than being lifted into the card header: the list above it is long enough that a header control would
 * scroll out of sight exactly when you needed it.
 */
export function StatusRolesPanel({
  instanceId,
  dialName,
  onChanged,
}: {
  instanceId: string;
  dialName: string;
  onChanged: () => void | Promise<void>;
}) {
  const [view, setView] = useState<InstanceStatusRolesView | null>(null);
  const [edits, setEdits] = useState<Record<string, string[] | null>>({});
  // KEY-918: who each status is for, beside its roles. Rides on the same override row, so a revert (an edit
  // of null) puts it back too.
  const [audienceEdits, setAudienceEdits] = useState<Record<string, StatusAudience>>({});
  // KEY-298: the action matrix (edit fields / upload / versions / post) edited alongside statuses.
  const [actionsView, setActionsView] = useState<InstanceActionRolesView | null>(null);
  const [actionEdits, setActionEdits] = useState<Record<string, string[] | null>>({});
  // KEY-285: the SECOND mask on the restricted-post action — who a sender may address. null = anyone who
  // can see the action point; an array is the mask (Admin included, since a dial admin reads restricted
  // messages whatever it says). A key present here means "changed", exactly like actionEdits.
  const [actionTargetEdits, setActionTargetEdits] = useState<Record<string, string[] | null>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      setView(await api.getInstanceStatusRoles(token, instanceId));
      setActionsView(await api.getInstanceActionRoles(token, instanceId));
      setError(null);
    } catch {
      setError("Couldn't load who can set each status.");
    }
  }, [instanceId]);

  useEffect(() => {
    void load();
  }, [load]);

  // What the row shows: a pending edit if there is one (null = "revert", i.e. back to the definition's
  // roles), otherwise what the server says is in force.
  function shown(palette: InstanceStatusRolePalette, status: InstanceStatusRole): string[] {
    const key = statusRoleKey(palette.statusSetId, status.statusKey);
    if (!(key in edits)) return status.effectiveRoles;
    const pending = edits[key];
    return pending === null ? Array.from(new Set([...status.definitionRoles, "Admin"])) : pending;
  }

  function toggle(palette: InstanceStatusRolePalette, status: InstanceStatusRole, role: string) {
    if (role === "Admin") return;
    const current = shown(palette, status);
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    setEdits((e) => ({ ...e, [statusRoleKey(palette.statusSetId, status.statusKey)]: Array.from(new Set([...next, "Admin"])) }));
  }

  function revert(palette: InstanceStatusRolePalette, status: InstanceStatusRole) {
    const key = statusRoleKey(palette.statusSetId, status.statusKey);
    setEdits((e) => ({ ...e, [key]: null }));
    setAudienceEdits((e) => {
      const { [key]: _dropped, ...rest } = e;
      return rest;
    });
  }

  const definitionAudience = (status: InstanceStatusRole): StatusAudience => status.definitionAudience ?? "Everyone";

  function shownAudience(palette: InstanceStatusRolePalette, status: InstanceStatusRole): StatusAudience {
    const key = statusRoleKey(palette.statusSetId, status.statusKey);
    if (key in audienceEdits) return audienceEdits[key];
    if (key in edits && edits[key] === null) return definitionAudience(status);
    return status.effectiveAudience ?? definitionAudience(status);
  }

  function chooseAudience(palette: InstanceStatusRolePalette, status: InstanceStatusRole, audience: StatusAudience) {
    const key = statusRoleKey(palette.statusSetId, status.statusKey);
    // A pending revert would delete the row the audience rides on: keep the roles it would have shown instead.
    if (key in edits && edits[key] === null) {
      setEdits((e) => ({ ...e, [key]: shown(palette, status) }));
    }
    setAudienceEdits((e) => ({ ...e, [key]: audience }));
  }

  // The action rows mirror the status rows exactly: pending edit first (null = revert to the
  // definition's roles), else what the server says is in force.
  function shownAction(a: InstanceActionRole): string[] {
    if (!(a.action in actionEdits)) return a.effectiveRoles;
    const pending = actionEdits[a.action];
    return pending === null ? Array.from(new Set([...a.definitionRoles, "Admin"])) : pending;
  }

  function toggleAction(a: InstanceActionRole, role: string) {
    if (role === "Admin") return;
    const current = shownAction(a);
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    setActionEdits((e) => ({ ...e, [a.action]: Array.from(new Set([...next, "Admin"])) }));
  }

  function revertAction(a: InstanceActionRole) {
    setActionEdits((e) => ({ ...e, [a.action]: null }));
    // One delete reverts both masks — the override row is the unit, so the pending target goes with it.
    setActionTargetEdits((e) => {
      const { [a.action]: _dropped, ...rest } = e;
      return rest;
    });
  }

  function shownTarget(a: InstanceActionRole): string[] | null {
    return a.action in actionTargetEdits ? actionTargetEdits[a.action] : (a.effectiveTargetRoles ?? null);
  }

  function toggleActionTarget(a: InstanceActionRole, role: string) {
    const current = shownTarget(a);
    if (current === null) {
      setActionTargetEdits((e) => ({ ...e, [a.action]: Array.from(new Set(["Admin", role])) }));
      return;
    }
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    const kept = Array.from(new Set([...next, "Admin"]));
    setActionTargetEdits((e) => ({ ...e, [a.action]: kept.length > 1 ? kept : null }));
  }

  async function save() {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const touchedStatuses = new Set([...Object.keys(edits), ...Object.keys(audienceEdits)]);
      if (touchedStatuses.size > 0) {
        await api.setInstanceStatusRoles(
          token,
          instanceId,
          [...touchedStatuses].map((key) => {
            const [statusSetId, statusKey] = key.split("::");
            const palette = view?.palettes.find((p) => p.statusSetId === statusSetId);
            const status = palette?.statuses.find((st) => st.statusKey === statusKey);
            const roles = key in edits ? edits[key] : palette && status ? shown(palette, status) : null;
            if (roles === null || !palette || !status) return { statusSetId, statusKey, roles: null };
            // Only an audience that differs from the definition's is pinned; the same one travels as null, so
            // a later definition change still flows through.
            const audience = shownAudience(palette, status);
            return { statusSetId, statusKey, roles, audience: audience === definitionAudience(status) ? null : audience };
          }),
        );
      }
      const touchedActions = new Set([...Object.keys(actionEdits), ...Object.keys(actionTargetEdits)]);
      if (touchedActions.size > 0) {
        await api.setInstanceActionRoles(
          token,
          instanceId,
          [...touchedActions].map((action) => {
            const a = actionsView?.actions.find((x) => x.action === action);
            const roles = action in actionEdits ? actionEdits[action] : (a ? shownAction(a) : null);
            // A null roles array deletes the override row, which reverts the target mask with it.
            if (roles === null) {
              return { action, roles: null };
            }
            const target = a ? shownTarget(a) : null;
            return { action, roles, targetRoles: target ?? [] };
          }),
        );
      }
      setEdits({});
      setAudienceEdits({});
      setActionEdits({});
      setActionTargetEdits({});
      await load();
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403 ? "You don't have permission to change this." : "Couldn't save those changes.");
    } finally {
      setBusy(false);
    }
  }

  const dirty =
    Object.keys(edits).length > 0 || Object.keys(audienceEdits).length > 0 || Object.keys(actionEdits).length > 0 || Object.keys(actionTargetEdits).length > 0;
  const canManage = view?.canManage === true;

  return (
    <SettingsCard title="Who can set each status" subtitle={`Roles that can move a status in “${dialName}”`}>
      {!view && !error && <p style={{ color: "var(--text-faint)", fontSize: 14, margin: "4px 0" }}>Loading…</p>}
      {view && !canManage && (
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--text-muted)" }}>
          Only a dial admin can change these. This is what applies today.
        </p>
      )}
      {view?.palettes.map((palette) => (
        <div key={palette.statusSetId} style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{palette.name}</div>
          {palette.usage && (
            <div style={{ fontSize: 12.5, color: "var(--text-faint)", marginBottom: 8 }}>{palette.usage}</div>
          )}
          {palette.statuses.map((status) => {
            const roles = shown(palette, status);
            const key = statusRoleKey(palette.statusSetId, status.statusKey);
            const audience = shownAudience(palette, status);
            const changedFromDefinition = key in edits ? edits[key] !== null : status.isOverridden || key in audienceEdits;
            return (
              <div
                key={status.statusKey}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", flexWrap: "wrap" }}
              >
                <span
                  aria-hidden
                  style={{ width: 12, height: 12, borderRadius: 99, background: status.colour, flex: "0 0 auto" }}
                />
                <span style={{ fontSize: 13.5, color: "var(--text)", minWidth: 120, flex: "1 1 120px" }}>{status.name}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  {INSTANCE_ROLE_NAMES.map((role) => {
                    const on = roles.includes(role);
                    const fixed = role === "Admin";
                    return (
                      <button
                        key={role}
                        type="button"
                        aria-pressed={on}
                        disabled={!canManage || fixed}
                        onClick={() => toggle(palette, status, role)}
                        title={fixed ? "An admin can always set any status" : `${on ? "Remove" : "Allow"} ${role} — ${instanceRoleDescription(role)}`}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: "4px 10px",
                          borderRadius: 99,
                          cursor: canManage && !fixed ? "pointer" : "default",
                          border: `var(--border-w) solid ${on ? "var(--accent)" : "var(--border)"}`,
                          background: on ? hexA("#2563eb", 0.14) : "var(--surface-2)",
                          color: on ? "var(--text)" : "var(--text-faint)",
                          opacity: fixed ? 0.75 : 1,
                        }}
                      >
                        {role}
                      </button>
                    );
                  })}
                </div>
                {/* KEY-918: who it is for, beside the roles — e.g. members only, so a guest Editor can't. */}
                <select
                  aria-label={`Who can set ${status.name}`}
                  value={audience}
                  disabled={!canManage}
                  onChange={(e) => chooseAudience(palette, status, e.target.value as StatusAudience)}
                  title="Beside the roles: members only, guests only, or only the person named Responsible there (where nobody is named, only an admin)"
                  style={{
                    fontSize: 12,
                    padding: "4px 8px",
                    borderRadius: 8,
                    border: `var(--border-w) solid ${audience === "Everyone" ? "var(--border)" : "var(--accent)"}`,
                    background: "var(--surface-2)",
                    color: "var(--text)",
                  }}
                >
                  {STATUS_AUDIENCES.map((a) => (
                    <option key={a} value={a}>{STATUS_AUDIENCE_LABELS[a]}</option>
                  ))}
                </select>
                {canManage && changedFromDefinition && (
                  <button
                    type="button"
                    onClick={() => revert(palette, status)}
                    style={{ border: "none", background: "none", cursor: "pointer", color: "var(--text-faint)", fontSize: 12, textDecoration: "underline", padding: 4 }}
                  >
                    Use the dial's default
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {actionsView && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>Other actions</div>
          <div style={{ fontSize: 12.5, color: "var(--text-faint)", marginBottom: 8 }}>
            Who may do everything else here. The definition sets the default; changes apply to this dial only.
          </div>
          {actionsView.actions.map((a) => {
            const meta = INSTANCE_ACTIONS.find((x) => x.name === a.action);
            const roles = shownAction(a);
            const changedFromDefinition = a.action in actionEdits ? actionEdits[a.action] !== null : a.isOverridden;
            return (
              <div key={a.action} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", flexWrap: "wrap" }}>
                <span style={{ fontSize: 13.5, color: "var(--text)", minWidth: 120, flex: "1 1 120px" }} title={meta?.description}>
                  {meta?.label ?? a.action}
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  {INSTANCE_ROLE_NAMES.map((role) => {
                    const on = roles.includes(role);
                    const fixed = role === "Admin";
                    return (
                      <button
                        key={role}
                        type="button"
                        aria-pressed={on}
                        disabled={!canManage || fixed}
                        onClick={() => toggleAction(a, role)}
                        title={fixed ? "An admin can always do this" : `${on ? "Remove" : "Allow"} ${role} — ${instanceRoleDescription(role)}`}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: "4px 10px",
                          borderRadius: 99,
                          cursor: canManage && !fixed ? "pointer" : "default",
                          border: `var(--border-w) solid ${on ? "var(--accent)" : "var(--border)"}`,
                          background: on ? hexA("#2563eb", 0.14) : "var(--surface-2)",
                          color: on ? "var(--text)" : "var(--text-faint)",
                          opacity: fixed ? 0.75 : 1,
                        }}
                      >
                        {role}
                      </button>
                    );
                  })}
                </div>
                {canManage && changedFromDefinition && (
                  <button
                    type="button"
                    onClick={() => revertAction(a)}
                    style={{ border: "none", background: "none", cursor: "pointer", color: "var(--text-faint)", fontSize: 12, textDecoration: "underline", padding: 4 }}
                  >
                    Use the dial's default
                  </button>
                )}
                {meta?.targetLabel && (
                  <div style={{ flex: "1 1 100%", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingLeft: 2 }}>
                    <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>{meta.targetLabel}</span>
                    <button
                      type="button"
                      aria-pressed={shownTarget(a) === null}
                      disabled={!canManage}
                      onClick={() => setActionTargetEdits((e) => ({ ...e, [a.action]: null }))}
                      style={targetChip(shownTarget(a) === null, !canManage)}
                    >
                      {meta.targetAnyLabel ?? "Anyone"}
                    </button>
                    {INSTANCE_ROLE_NAMES.map((role) => {
                      const mask = shownTarget(a);
                      const on = mask !== null && mask.includes(role);
                      const fixed = role === "Admin" && mask !== null;
                      return (
                        <button
                          key={role}
                          type="button"
                          aria-pressed={on}
                          disabled={!canManage || fixed}
                          onClick={() => toggleActionTarget(a, role)}
                          title={fixed ? "A dial admin can read restricted messages anyway" : instanceRoleDescription(role)}
                          style={targetChip(on, !canManage || fixed)}
                        >
                          {role}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {error && <p style={{ margin: "0 0 8px", fontSize: 12.5, color: "var(--st-overdue)" }}>{error}</p>}
      {canManage && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            className="kd-btn"
            onClick={() => {
              setEdits({});
              setActionEdits({});
              setActionTargetEdits({});
            }}
            disabled={!dirty || busy}
          >
            Discard
          </button>
          <button className="kd-btn-primary" onClick={() => void save()} disabled={!dirty || busy}>Save</button>
        </div>
      )}
    </SettingsCard>
  );
}

/** Chip styling for the restricted-post target sub-line (KEY-285), matched to the role chips above it. */
function targetChip(on: boolean, locked: boolean) {
  return {
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 99,
    cursor: locked ? "default" : "pointer",
    border: `var(--border-w) solid ${on ? "var(--accent)" : "var(--border)"}`,
    background: on ? hexA("#2563eb", 0.14) : "var(--surface-2)",
    color: on ? "var(--text)" : "var(--text-faint)",
  } as const;
}

// ---- Notifications slide-over (illustrative) ----

interface SampleNotification {
  who: Person;
  text: string;
  when: string;
  unread: boolean;
  dot?: string;
}

const SAMPLE_PEOPLE = {
  jm: personFrom({ email: "james.mboya@example.org", displayName: "James Mboya" }),
  db: personFrom({ email: "daniel.brooks@example.org", displayName: "Daniel Brooks" }),
  ao: personFrom({ email: "aisha.okafor@example.org", displayName: "Aisha Okafor" }),
  pr: personFrom({ email: "priya.raman@example.org", displayName: "Priya Raman" }),
};

const NOTIFICATIONS: SampleNotification[] = [
  { who: SAMPLE_PEOPLE.jm, text: "flagged “Authorised signatory list current” as At risk", when: "2 hours ago", unread: true, dot: "#f97316" },
  { who: SAMPLE_PEOPLE.db, text: "requested additional records on “Independent examination complete”", when: "1 day ago", unread: true, dot: "#e7161b" },
  { who: SAMPLE_PEOPLE.ao, text: "completed “Designated safeguarding lead appointed”", when: "2 days ago", unread: false, dot: "#3eb034" },
  { who: SAMPLE_PEOPLE.pr, text: "uploaded Trustee Register 2026.pdf", when: "3 days ago", unread: false },
];

export function NotificationsModal({ onClose }: { onClose: () => void }) {
  return (
    <SlideOver title="Notifications" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {NOTIFICATIONS.map((n, i) => (
          <div
            key={i}
            className="kd-row-hover"
            style={{
              display: "flex",
              gap: 12,
              padding: "14px 12px",
              borderRadius: "var(--radius)",
              cursor: "pointer",
              background: n.unread ? "var(--accent-soft)" : "transparent",
            }}
          >
            <Avatar person={n.who} size={36} ring={false} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text)" }}>
                <span style={{ fontWeight: 700 }}>{n.who.name}</span> {n.text}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 3, display: "flex", alignItems: "center", gap: 7 }}>
                {n.dot && <span style={{ width: 7, height: 7, borderRadius: 99, background: n.dot }} />}
                {n.when}
              </div>
            </div>
            {n.unread && <span style={{ width: 8, height: 8, borderRadius: 99, background: "var(--accent)", marginTop: 6, flex: "none" }} />}
          </div>
        ))}
      </div>
    </SlideOver>
  );
}

// ---- Help videos panel (floating, draggable; KEY-393) ----

interface HelpVideo {
  id: string;
  title: string;
  /** No real links are wired up yet — the submitter supplies these separately. A null url shows a
   *  "Coming soon" row rather than a dead click. */
  url: string | null;
}

const HELP_VIDEOS: HelpVideo[] = [
  { id: "welcome", title: "Welcome to Key Dials", url: null },
  { id: "reading-the-dial", title: "Reading the dial", url: null },
  { id: "working-an-action-point", title: "Working an action point", url: null },
];

function PlayGlyph() {
  return (
    <div
      style={{
        width: 0,
        height: 0,
        borderLeft: "11px solid #fff",
        borderTop: "7px solid transparent",
        borderBottom: "7px solid transparent",
        marginLeft: 3,
      }}
    />
  );
}

/**
 * Help videos, wired up as a floating panel rather than `Modal`'s fixed, centred dialog — so it can be
 * dragged clear of whatever it would otherwise cover (KEY-393). Position persists via
 * {@link useDraggableDock}, exactly like web-admin's feedback dock.
 *
 * No real video links exist yet: every row ships with `url: null` and renders "Coming soon" until the
 * submitter supplies them. A row with a url plays inline, one at a time, via the same
 * `VideoPlayer`/`classifyVideoUrl` pair the per-dial guidance videos already use.
 */
export function HelpModal({ onClose }: { onClose: () => void }) {
  const { position, dragging, elementRef, handleProps } = useDraggableDock(HELP_VIDEOS_DOCK_POSITION_KEY);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const placement = position ? { left: position.x, top: position.y } : { right: 16, top: 76 };

  return (
    <div
      ref={elementRef}
      role="dialog"
      aria-label="Help videos"
      tabIndex={-1}
      // On the panel, not window: a window-level listener would also reach Modal/SlideOver and close
      // more than one thing at once.
      onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } }}
      style={{
        position: "fixed",
        ...placement,
        zIndex: 100,
        width: 380,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100vh - 96px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: "var(--radius-lg)",
        border: "var(--border-w) solid var(--border)",
        background: "var(--surface)",
        boxShadow: "var(--shadow-lg)",
      }}
    >
      <div
        {...handleProps}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 12px 14px 16px",
          borderBottom: "var(--border-w) solid var(--border)",
          touchAction: "none",
          userSelect: "none",
          cursor: dragging ? "grabbing" : "grab",
        }}
      >
        <GripGlyph />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "var(--text)", flex: 1 }}>Help videos</h2>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            width: 32,
            height: 32,
            borderRadius: 99,
            border: "none",
            background: "var(--surface-inset)",
            color: "var(--text-muted)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
          }}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      <div style={{ padding: 14, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        {HELP_VIDEOS.map((video) => {
          const classified = video.url ? classifyVideoUrl(video.url) : null;
          const expanded = expandedId === video.id && classified !== null;
          return (
            <div
              key={video.id}
              style={{
                borderRadius: "var(--radius)",
                border: "var(--border-w) solid var(--border)",
                background: "var(--surface-2)",
                overflow: "hidden",
              }}
            >
              <button
                type="button"
                className={video.url ? "kd-row-hover" : undefined}
                onClick={() => { if (video.url) setExpandedId((cur) => (cur === video.id ? null : video.id)); }}
                disabled={!video.url}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  width: "100%",
                  padding: 12,
                  border: "none",
                  background: "none",
                  font: "inherit",
                  textAlign: "left",
                  cursor: video.url ? "pointer" : "default",
                }}
              >
                <div
                  style={{
                    width: 72,
                    height: 46,
                    borderRadius: "var(--radius-sm)",
                    background: "var(--ring-section)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "none",
                    opacity: video.url ? 1 : 0.55,
                  }}
                >
                  <PlayGlyph />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14.5, color: "var(--text)" }}>{video.title}</div>
                </div>
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: video.url ? "var(--accent-text)" : "var(--text-faint)",
                    flex: "none",
                  }}
                >
                  {video.url ? (expanded ? "Hide" : "Watch") : "Coming soon"}
                </span>
              </button>
              {expanded && classified && (
                <div style={{ padding: "0 12px 12px" }}>
                  <VideoPlayer video={{ title: video.title, embedKind: classified.embedKind, playbackUrl: classified.playbackUrl }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
